import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PogLogo from "./PogLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_TYPES = [
  { value: "wholesale", label: "Wholesale" },
  { value: "press", label: "Press" },
  { value: "investor", label: "Investor" },
  { value: "contact", label: "General" },
];

const initial = {
  name: "",
  company: "",
  role: "",
  email: "",
  phone: "",
  market: "",
  inquiry_type: "wholesale",
  message: "",
};

const Inquire = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.message) {
      toast.error("Please fill in name, company, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `Request failed: ${res.status}`);
      }
      setSubmitted(true);
      setForm(initial);
      toast.success("Mahalo. We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquire"
      data-testid="inquire-section"
      className="relative py-32 md:py-56 bg-[#1E1712] text-[#EFE7D6] overflow-hidden"
    >
      {/* Sunburst as very subtle background element */}
      <div className="absolute -top-[30%] -right-[20%] w-[70vw] h-[70vw] opacity-[0.07] pointer-events-none">
        <div className="sunburst rounded-full absolute inset-0" style={{ filter: "blur(8px)" }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="grid md:grid-cols-12 gap-14 md:gap-20 mb-20"
        >
          <div className="md:col-span-5">
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">09 — Contact</span>
              <span className="w-16 hairline-strong h-px inline-block" />
              <span className="overline">Inquire</span>
            </div>
            <h2
              className="display-tight text-4xl md:text-7xl"
              data-testid="inquire-headline"
            >
              Carry POG.
              <br />
              <span className="italic text-[#DCCFB4]">Cover POG.</span>
              <br />
              <span className="italic text-[#E85A15]">Back POG.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-24">
            <p className="font-body text-xl md:text-2xl text-[#EFE7D6]/90 leading-[1.5]">
              Retailers, press, investors — tell us who you are and we'll send
              the right materials within 48 hours.
            </p>
          </div>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center py-24 border-t border-b border-[#EFE7D6]/15"
            data-testid="inquire-success"
          >
            <div className="mb-8 flex justify-center">
              <PogLogo size={80} />
            </div>
            <h3 className="display-tight text-4xl md:text-6xl mb-6">
              Mahalo nui loa.
            </h3>
            <p className="font-body text-xl text-[#DCCFB4]/85 max-w-md mx-auto leading-relaxed">
              We've received your inquiry. The POG Spirits team will be in touch
              within two business days.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="btn-ghost mt-12"
              data-testid="inquire-reset"
            >
              Send Another
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            onSubmit={submit}
            className="grid md:grid-cols-12 gap-10 md:gap-16 border-t border-[#EFE7D6]/15 pt-16"
            data-testid="inquiry-form"
          >
            <div className="md:col-span-4">
              <div className="overline mb-6">I am inquiring as a</div>
              <div className="flex flex-col gap-3">
                {INQUIRY_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setForm({ ...form, inquiry_type: t.value })}
                    data-testid={`inquiry-type-${t.value}`}
                    className={`text-left flex items-baseline gap-4 group transition-colors ${
                      form.inquiry_type === t.value
                        ? "text-[#E85A15]"
                        : "text-[#EFE7D6]/60 hover:text-[#EFE7D6]"
                    }`}
                  >
                    <span
                      className={`counter transition-colors ${
                        form.inquiry_type === t.value
                          ? "text-[#E85A15]"
                          : "text-[#DCCFB4]/40"
                      }`}
                    >
                      —
                    </span>
                    <span className="font-body text-[11px] tracking-[0.28em] uppercase font-medium">
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-8 space-y-8">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                <div>
                  <label className="overline block mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="input-name"
                    className="editorial-input"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Company *</label>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={update("company")}
                    data-testid="input-company"
                    className="editorial-input"
                    placeholder="Business or publication"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Role</label>
                  <input
                    type="text"
                    value={form.role}
                    onChange={update("role")}
                    data-testid="input-role"
                    className="editorial-input"
                    placeholder="Buyer · Editor · Partner"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Market</label>
                  <input
                    type="text"
                    value={form.market}
                    onChange={update("market")}
                    data-testid="input-market"
                    className="editorial-input"
                    placeholder="O‘ahu · Maui · West Coast"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="input-email"
                    className="editorial-input"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="input-phone"
                    className="editorial-input"
                    placeholder="+1 (808) …"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Message *</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  className="editorial-input resize-none"
                  placeholder="Tell us about your store, publication, or interest in POG…"
                />
              </div>

              <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-[#EFE7D6]/15">
                <p className="font-body text-[11px] tracking-[0.15em] uppercase text-[#DCCFB4]/50 max-w-md leading-relaxed pt-6">
                  By submitting, you confirm you are 21+ and agree to be
                  contacted by the POG Spirits team.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary is-solid mt-6 sm:mt-0"
                  data-testid="inquiry-submit"
                >
                  {submitting ? "Sending…" : "Send Inquiry"}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default Inquire;
